import { useState, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ACCOUNT_ROLES } from '../lib/environment-config';

// TODO: Why are there conflicting ISigner types?
import type { ISigner as BuilderISigner } from '../store/senderBuild/types';
import type { ISigner as RecipientsISigner, IRecipient, IRecipientBody } from './recipients/types';

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const chameleonElId = (num: string | number) => {
  return `ds-chameleon-${num}`;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const checkIfRecipientIsSender = (signer: RecipientsISigner | BuilderISigner | IRecipient | IRecipientBody) => {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  return signer.title?.toLowerCase() === 'me' || signer.recipientColorCounter === 0;
};

/**
 * Is this recipient row the sender's own? Spelled once because several places
 * route on it, and when they disagree the sender is sent down a path that was
 * built for somebody else.
 */
export function isSenderOwnSignerRow(
  signer: { title?: string; email?: string; recipientColorCounter?: number },
  senderEmail?: string
) {
  return Boolean(
    // Structural on purpose so every caller's row shape fits; the single cast
    // lives here rather than at each call site.
    checkIfRecipientIsSender(signer as IRecipientBody) &&
    signer.email &&
    senderEmail &&
    signer.email.toLowerCase() === senderEmail.toLowerCase()
  );
}

// https://dev.to/gabe_ragland/debouncing-with-react-hooks-jci
// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const useDebounce = (value: any, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [value]);

  return debouncedValue;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const convertFieldTypeToQueryType = (fieldType: string): string => {
  if (fieldType === 'First Name') {
    return 'firstName';
  }
  if (fieldType === 'Last Name') {
    return 'lastName';
  }
  if (fieldType === 'Email') {
    return 'email';
  }
  return '';
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const createRandomId = (): string => uuidv4();

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const removeSpecialCharacters = (name: string) => {
  // this will replace all non-alphanumeric characters, except those included in the square brackets after 9, with an underscore
  const cleanName = name.replace(/[^a-zA-Z0-9_.-]/g, '_');
  return cleanName;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getUserAdminRoles = async (
  setUser: React.Dispatch<
    React.SetStateAction<{
      isBrokerAdmin: boolean;
      isGroupAdmin: boolean;
    }>
  >,
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  getUsersGroups
) => {
  const groupsRes = await getUsersGroups();

  if (groupsRes.userGroups && groupsRes.userGroups.length > 0 && groupsRes.access && groupsRes.access.length > 0) {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const parentGroups = groupsRes.userGroups?.filter((g) => !g.parentGroupId).map((g) => g._id);

    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const parentGroupRoles = groupsRes.access.filter((a) => parentGroups.includes(a.groupId)).map((a) => a.roleId);

    setUser({
      isBrokerAdmin: parentGroupRoles.includes(ACCOUNT_ROLES.BROKER_ADMIN),
      isGroupAdmin: parentGroupRoles.includes(ACCOUNT_ROLES.GROUP_ADMIN),
    });
  } else {
    setUser({
      isBrokerAdmin: false,
      isGroupAdmin: false,
    });
  }
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const useIsSkySlopeMobileApp = (): boolean => {
  const userAgent = typeof window.navigator === 'undefined' ? '' : navigator.userAgent;
  return useMemo(() => Boolean(userAgent.match(/MobileApp.*SkySlope/i)), [userAgent]);
};

/**
 * @deprecated Use postMessageToMobileApp from '../utils/post-message' instead.
 * This function will be removed in a future version.
 *
 * Post a message to the mobile app webkit handler.
 */
export function postWindowMessage(
  body:
    | {
        header_text: string;
        back_button_type: string;
        should_back_button_exit: boolean;
        button_text: string;
        metadata?: {
          digisignEnvelopeId: string;
        };
      }
    | {
        status: string;
        message?: string;
        // added twice to keep things backward compatible
        metadata?: {
          digisignEnvelopeId: string;
        };
      }
) {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  window.webkit.messageHandlers.mobileAppListener.postMessage(JSON.stringify(body));
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const isAppRunningInIframe = (): boolean => window.location !== window.parent.location;

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const isCurrentDatePastExpDate = (expirationDate: string) => {
  // expirationDate is currently either subscription.validUntil or trial.endDate
  // and is in the format 'yyyy-mm-dd'
  const currentDate = new Date().toISOString().slice(0, 10);
  return currentDate >= expirationDate;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const formatDateToYYYYMMDD = (dateString: string) => {
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};
